import { Injectable } from '@angular/core';
import {
    Chart,
    Plugin,
    // Controllers
    LineController,
    BarController,
    DoughnutController,
    PieController,
    PolarAreaController,
    RadarController,
    ScatterController,
    BubbleController,
    // Elements
    ArcElement,
    LineElement,
    BarElement,
    PointElement,
    // Scales
    CategoryScale,
    LinearScale,
    LogarithmicScale,
    RadialLinearScale,
    TimeScale,
    TimeSeriesScale,
    // Plugins
    Tooltip,
    Legend,
    Title,
    Filler,
} from 'chart.js';
import { ChartJsProvider } from './providers/charts.provider';

// Register moment.js date adapter for Chart.js v3 time scales
// This import has a side effect - it registers the adapter with Chart.js
import 'chartjs-adapter-moment';

// Register all Chart.js components
// In Chart.js v3, components must be explicitly registered unlike v2 where everything was auto-registered
Chart.register(
    // Controllers
    LineController,
    BarController,
    DoughnutController,
    PieController,
    PolarAreaController,
    RadarController,
    ScatterController,
    BubbleController,
    // Elements
    ArcElement,
    LineElement,
    BarElement,
    PointElement,
    // Scales
    CategoryScale,
    LinearScale,
    LogarithmicScale,
    RadialLinearScale,
    TimeScale,
    TimeSeriesScale,
    // Plugins
    Tooltip,
    Legend,
    Title,
    Filler,
);

// Set Proxima Nova as the global default font for all Chart.js instances
Chart.defaults.font.family = '\'Proxima Nova\', sans-serif';

@Injectable({
    providedIn: 'root',
})
export class ChartsRunService {
    constructor (
        private readonly chartJsProvider: ChartJsProvider,
    ) {}

    initializeApp (): void {
        this.chartJsProvider.setOptions({
            colors: ['#803690', '#00ADF9', '#DCDCDC', '#46BFBD', '#FDB45C', '#949FB1', '#4D5360'],
        });

        let onLeave = false;
        // Draw background colors for line chart
        const backgroundPlugin: Plugin = {
            id: 'monsido-background',
            beforeDraw: function (chart: any) {
                let width; let height; let ctx;

                if (chart.options.backgroundAreas) {
                    const chartArea = chart.chartArea;
                    const chatAreaWidth = chartArea.right - chartArea.left;
                    const backgroundAreas = chart.options.backgroundAreas;
                    // In v3, scale IDs are just 'y' instead of 'y-axis-0'
                    const yScale = chart.scales['y-axis-0'] || chart.scales['y'];
                    ctx = chart.ctx;
                    ctx.save();

                    for (let i = 0; i < backgroundAreas.length; i++) {
                        const area = backgroundAreas[i];
                        const top = yScale.getPixelForValue(area.range[1]);

                        height = yScale.getPixelForValue(area.range[0]) - top;

                        ctx.fillStyle = area.color;
                        ctx.fillRect(chartArea.left, top, chatAreaWidth, height);

                        ctx.restore();
                    }
                }

                if (chart.config.type === 'doughnut' || chart.config.type === 'pie') {
                    // Ensure square canvas; only apply when the container dictates the size
                    // (i.e. local chartBase charts). ng2-charts-based charts handle this themselves.
                    if (!chart.canvas || !chart.canvas.hasAttribute('basechart')) {
                        chart.options.maintainAspectRatio = true;
                        chart.options.aspectRatio = 1;
                    }
                }

                if (chart.config.type === 'doughnut' && chart.options.centerText) {
                    // Skip charts rendered by ng2-charts (baseChart directive, e.g. mon-doughnut-chart
                    // from @monsido/angular-shared-components) — they have their own centerText plugin
                    // registered via [plugins]="...", so drawing here would duplicate the label.
                    if (chart.canvas && chart.canvas.hasAttribute('basechart')) {
                        return;
                    }
                    // Draw text inside doughnut chart
                    width = chart.width;
                    height = chart.height;
                    ctx = chart.ctx;
                    const lowestRange = width > height ? height : width;

                    // In Chart.js v3, chart.width/height are CSS pixels
                    // The canvas context handles DPR scaling internally, so we use CSS pixels directly
                    const centerText = chart.options.centerText;
                    let hasPercentage = false;
                    let hasTotalLabel = false;
                    let hasSubLabel = false;
                    const totalFontSize = Number(
                        centerText.totalFontSize
                            ? (lowestRange / centerText.totalFontSize).toFixed(2)
                            : (lowestRange / 250).toFixed(2),
                    );
                    const percentageFontSize = Number(
                        centerText.percentageFontSize
                            ? (lowestRange / centerText.percentageFontSize).toFixed(2)
                            : (lowestRange / 100).toFixed(2),
                    );
                    const subFontSize = centerText.subFontSize ? (lowestRange / centerText.subFontSize).toFixed(2) : (lowestRange / 250).toFixed(2);

                    const totalFontWeight = centerText.totalFontWeight || 300;
                    const percentageFontWeight = centerText.percentageFontWeight || 400;
                    const subFontWeight = centerText.subFontWeight || 300;
                    let percentageLabel;
                    let totalLabel;
                    let subLabel;

                    if (centerText.percentageLabel) {
                        hasPercentage = true;
                        percentageLabel = removeDecimals(centerText.percentageLabel);
                    }
                    if (centerText.totalLabel) {
                        hasTotalLabel = true;
                        totalLabel = centerText.totalLabel;
                    }
                    if (centerText.subLabel) {
                        hasSubLabel = true;
                        subLabel = centerText.subLabel;
                    }

                    // In Chart.js v3, get the inner radius from the arc element metadata
                    // The inner radius defines the "hole" of the doughnut where we draw text
                    let innerRadius = 0;
                    try {
                        const meta = chart.getDatasetMeta(0);
                        if (meta && meta.data && meta.data[0]) {
                            innerRadius = (meta.data[0] as any).innerRadius || 0;
                        }
                    } catch (e) {
                        // Fallback if meta data is not available
                    }
                    // Fallback to legacy calculation if innerRadius not found
                    const radiusLength = innerRadius || (chart as any).radiusLength || chart.chartArea.right / 4;
                    // Calculate available width for center text (diameter of inner circle minus padding)
                    const centerLineLength = innerRadius > 0
                        ? innerRadius * 2 * 0.85 // Use 85% of inner diameter for text
                        : width - 2 * radiusLength - height / 10;

                    // Draw only percentage label
                    if (hasPercentage && !hasTotalLabel && !hasSubLabel) {
                        ctx.restore();

                        ctx.textBaseline = 'middle';
                        drawText(chart, ctx, percentageLabel, percentageFontSize, percentageFontWeight, 0.5, false);

                        ctx.save();
                    }

                    // Draw only total (main) label
                    if (!hasPercentage && hasTotalLabel && !hasSubLabel) {
                        ctx.restore();

                        ctx.textBaseline = 'middle';
                        drawText(chart, ctx, totalLabel, totalFontSize, totalFontWeight, 0.5, centerLineLength);

                        ctx.save();
                    }

                    if (hasPercentage && hasTotalLabel && !hasSubLabel) {
                        ctx.restore();

                        ctx.textBaseline = 'middle';
                        drawText(chart, ctx, percentageLabel, percentageFontSize, percentageFontWeight, 0.4, false);

                        ctx.save();

                        drawText(chart, ctx, totalLabel, totalFontSize, totalFontWeight, 0.505 + totalFontSize * 10 / height, centerLineLength);
                        ctx.restore();

                        // Draw horizontal line
                        drawLine(ctx, radiusLength + height / 20, height / 2, width - radiusLength - height / 20, height / 2);

                        ctx.save();
                    }

                    // Draw all labels and horizontal line
                    if (hasPercentage && hasTotalLabel && hasSubLabel) {
                        ctx.restore();

                        ctx.textBaseline = 'middle';

                        drawText(chart, ctx, percentageLabel, percentageFontSize, percentageFontWeight, 0.4, false);

                        ctx.save();

                        drawText(chart, ctx, totalLabel, totalFontSize, totalFontWeight, 0.505 + totalFontSize * 10 / height, centerLineLength);
                        ctx.restore();
                        drawText(
                            chart,
                            ctx,
                            subLabel,
                            subFontSize,
                            subFontWeight,
                            0.515 + totalFontSize * 15 / height + Number(subFontSize) * 10 / height,
                            centerLineLength,
                        );

                        ctx.restore();

                        // Draw horizontal line
                        drawLine(ctx, radiusLength + height / 20, height / 2, width - radiusLength - height / 20, height / 2);

                        ctx.save();
                    }
                }
            },
            beforeDatasetsDraw: function (chart: any) {
                const ctx = chart.ctx;
                if (chart.options.horizontalLines && chart.options.horizontalLines.length > 0) {
                    // In v3, scale IDs are just 'y' and 'x' instead of 'y-axis-0' and 'x-axis-0'
                    const yScale = chart.scales['y-axis-0'] || chart.scales['y'];
                    const xScale = chart.scales['x-axis-0'] || chart.scales['x'];
                    let yValue;
                    let line;

                    for (let i = 0; i < chart.options.horizontalLines.length; i++) {
                        line = chart.options.horizontalLines[i];
                        ctx.save();

                        yValue = yScale.getPixelForValue(line.value);
                        drawLine(ctx, xScale.getPixelForTick(0), yValue, chart.chartArea.right, yValue, line.style, line.width);

                        ctx.restore();
                    }

                }
            },
            afterDatasetsDraw: function (chart: any) {
                const ctx = chart.ctx;
                const options = chart.options;

                if (options.drawLabelOnChart) {
                    chart.data.datasets.forEach(function (dataset, i) {
                        const meta = chart.getDatasetMeta(i);
                        if (!meta.hidden) {
                            meta.data.forEach(function (element, index) {
                                const dataString = dataset.data[index].toString();
                                const padding = 5;
                                const position = element.tooltipPosition();

                                ctx.font = '400 1.1em Proxima Nova';
                                ctx.textAlign = 'center';
                                ctx.textBaseline = 'middle';
                                ctx.fillStyle = 'rgb(166,166,166)';

                                ctx.fillText(dataString, position.x, position.y - 8 - padding);
                            });
                        }
                    });
                }
            },
            afterDraw: function (chart: any) {
                const ctx = chart.ctx;
                if (chart.data) {
                    const horizontalLinesToDraw: any[] = [];
                    const hiddenLines: any[] = [];
                    chart.legend?.legendItems?.forEach(function (legendItem, index) {
                        if (legendItem.hidden === true) {
                            hiddenLines.push(index);
                        }
                    });
                    chart.data.datasets.forEach(function (dataset: any, index) {
                        if (dataset.data.length === 1 && dataset.type === 'line' && hiddenLines.indexOf(index) === -1) {
                            horizontalLinesToDraw.push({
                                value: dataset.data[0],
                                // In v3, default axis IDs are 'x' and 'y' instead of 'x-axis-0' and 'y-axis-0'
                                xScale: dataset.xAxisID || 'x',
                                yScale: dataset.yAxisID || 'y',
                                color: dataset.borderColor,
                            });
                        }
                    });
                    horizontalLinesToDraw.forEach(function (line) {
                        ctx.save();
                        drawLine(ctx,
                            chart.scales[line.xScale].left,
                            chart.scales[line.yScale].getPixelForValue(line.value),
                            chart.scales[line.xScale].right,
                            chart.scales[line.yScale].getPixelForValue(line.value),
                            line.color);
                        ctx.restore();
                    });
                }
            },
        };

        Chart.register(backgroundPlugin);


        // Vertical lines plugin
        const pluginState: any = {
            verticalLines: {
                currentLinesPosition: [],
                lastLinesPosition: [],
                lastLinesTextBox: [],
                tooltips: [],
                currentChartId: null,
            },
        };

        const VERTICAL_LINE_DEFAULTS = {
            LINE_DASH: [5, 5] as number[],
            LINE_COLOR: '#000000',
            LINE_WIDTH: 3,
            ADDITION_PADDING_TOP: 20,
            CIRCLE_TOP: -10,
            CIRCLE_RADIUS: 6,
            CIRCLE_HOVER_RADIUS: 7,
        };

        const verticalLinePlugin: Plugin = {
            id: 'monsido-vertical-lines',
            afterInit: function (chart) {
                if (hasVerticalLinePlugin(chart)) {
                    verticalLinePluginInit(chart);
                }
            },
            afterDatasetsDraw: function (chart) {
                if (hasVerticalLinePlugin(chart)) {
                    verticalLinePluginDraw(chart);
                    verticalLinePluginAfterDraw(chart);
                }
            },
            destroy: function () {
                hideVerticalLineTooltip();
                pluginState.verticalLines.currentLinesPosition = [];
                pluginState.verticalLines.lastLinesPosition = [];
                pluginState.verticalLines.lastLinesTextBox = [];
                pluginState.verticalLines.tooltips = [];
            },
            beforeEvent: function (chart, args) {
                if (onLeave) {
                    const event = args.event;
                    (event.native?.target as HTMLElement).style.cursor = 'default';
                    onLeave = false;
                }
            },
            afterEvent: function (chart, args) {
                if (hasVerticalLinePlugin(chart)) {
                    verticalLinePluginEventHandler(chart, args.event);
                }
            },
        };

        Chart.register(verticalLinePlugin);

        function verticalLinePluginInit (chart) {
            // Add padding to the top of the chart
            // To have more space to render the vertical line label
            if (!chart.options.layout) {
                chart.options.layout = { padding: { top: 0 } };
            }
            if (!chart.options.layout.padding) {
                chart.options.layout.padding = { top: 0 };
            }
            if (typeof chart.options.layout.padding === 'number') {
                chart.options.layout.padding = { top: chart.options.layout.padding };
            }
            (chart.options.layout.padding as any).top += VERTICAL_LINE_DEFAULTS.ADDITION_PADDING_TOP;
        }

        function verticalLinePluginDraw (chart) {
            const ctx = chart.ctx;
            const verticalLines = chart.options.verticalLines;
            let firstXScale;
            for (const id in chart.scales) {
                if (chart.scales.hasOwnProperty(id)) {
                    const s = chart.scales[id];
                    // In v3, check axis property instead of _horizontal
                    if (s.axis === 'x' || s.position === 'bottom') {
                        firstXScale = s;
                        break;
                    }
                }
            }
            verticalLines.lines.forEach(function (line, index) {
                let xScale;
                if (line.xAxisID) {
                    xScale = chart.scales[line.xAxisID];
                } else {
                    // If xAxisID is not defined, use the first x axis scale
                    xScale = firstXScale;
                }
                // Render line
                let lineX = xScale.getPixelForValue(line.x);
                if (isNaN(lineX) && xScale.ticks && xScale.ticks.length > 0) {
                    // Unable to get pixel for provided value
                    // Possibly that the scale isn't a timescale (it's a category scale)
                    // Need to provide timeScale value else the line unable to be drawn correctly
                    if (!verticalLines.timeScale) {
                        throw new Error('The xScale isn\'t a time scale, "verticalLines.timeScale" value need to be provided. The value is an array of date string with the same length as the labels provided to the chart');
                    }
                    const timeScale = verticalLines.timeScale;
                    const xDateTimestamp = new Date(line.x).getTime();
                    const totalDataPoints = timeScale.length;
                    let prevDataIndex = 0;
                    let prevTickTimestamp = new Date(timeScale[0]).getTime();

                    // Helper function to get pixel position for a data index
                    // In Chart.js v3, for category scales we need to use getPixelForDecimal
                    // which takes a value between 0 and 1 representing position along the axis
                    const getPixelForDataIndex = (dataIndex) => {
                        // Convert data index to decimal position (0 to 1)
                        // For category scales, each data point is evenly spaced
                        const decimal = totalDataPoints > 1 ? dataIndex / (totalDataPoints - 1) : 0.5;
                        return xScale.getPixelForDecimal(decimal);
                    };

                    if (xDateTimestamp < prevTickTimestamp) {
                        // lineX is positioned before the first data point
                        lineX = (xScale.left + getPixelForDataIndex(0)) / 2 - 10;
                    } else {
                        for (let i = 1; i < timeScale.length; i++) {
                            const tickTimestamp = new Date(timeScale[i]).getTime();
                            if (prevTickTimestamp === xDateTimestamp) {
                                lineX = getPixelForDataIndex(i - 1);
                                break;
                            }

                            if (tickTimestamp === xDateTimestamp) {
                                lineX = getPixelForDataIndex(i);
                                break;
                            }

                            if (prevTickTimestamp < xDateTimestamp && xDateTimestamp < tickTimestamp) {
                                // lineX is positioned between data points - interpolate
                                const boundStart = getPixelForDataIndex(prevDataIndex);
                                const boundEnd = getPixelForDataIndex(i);
                                // Calculate proportional position between the two data points
                                const totalTimeDiff = tickTimestamp - prevTickTimestamp;
                                const targetTimeDiff = xDateTimestamp - prevTickTimestamp;
                                const ratio = targetTimeDiff / totalTimeDiff;
                                lineX = boundStart + (boundEnd - boundStart) * ratio;
                                break;
                            }
                            prevDataIndex = i;
                            prevTickTimestamp = tickTimestamp;
                        }
                        if (isNaN(lineX)) {
                            // lineX is positioned after the last data point
                            lineX = (xScale.right + getPixelForDataIndex(totalDataPoints - 1)) / 2 + 10;
                        }
                    }
                }

                const originalStyle = line.style || {};
                const lineStyle = {
                    lineDash: originalStyle.lineDash || VERTICAL_LINE_DEFAULTS.LINE_DASH,
                    color: originalStyle.color || VERTICAL_LINE_DEFAULTS.LINE_COLOR,
                    width: originalStyle.width || VERTICAL_LINE_DEFAULTS.LINE_WIDTH,
                };
                const start = {
                    x: lineX,
                    y: xScale.top,
                };
                const end = {
                    x: lineX,
                    y: chart.chartArea.top,
                };
                ctx.save();
                ctx.beginPath();
                ctx.setLineDash(lineStyle.lineDash);
                ctx.moveTo(start.x, start.y);
                ctx.lineTo(end.x, end.y);
                ctx.strokeStyle = lineStyle.color;
                ctx.lineWidth = lineStyle.width;
                ctx.stroke();
                ctx.restore();

                // Render circles
                ctx.save();
                const fillStyle = line.label.color || lineStyle.color || VERTICAL_LINE_DEFAULTS.LINE_COLOR;
                const strokeStyle = line.label.color || lineStyle.color || VERTICAL_LINE_DEFAULTS.LINE_COLOR;

                ctx.fillStyle = fillStyle;
                ctx.strokeStyle = strokeStyle;
                ctx.beginPath();
                ctx.arc(lineX, end.y + VERTICAL_LINE_DEFAULTS.CIRCLE_TOP, VERTICAL_LINE_DEFAULTS.CIRCLE_RADIUS, 0, 2 * Math.PI);
                ctx.stroke();
                ctx.fill();

                ctx.restore();
                const hoverDelta = VERTICAL_LINE_DEFAULTS.CIRCLE_HOVER_RADIUS - VERTICAL_LINE_DEFAULTS.CIRCLE_RADIUS;
                pluginState.verticalLines.lastLinesTextBox[index] = {
                    orgX: lineX - VERTICAL_LINE_DEFAULTS.CIRCLE_RADIUS,
                    orgY: end.y + VERTICAL_LINE_DEFAULTS.CIRCLE_TOP - VERTICAL_LINE_DEFAULTS.CIRCLE_RADIUS,
                    x: lineX - VERTICAL_LINE_DEFAULTS.CIRCLE_RADIUS - hoverDelta,
                    y: end.y + VERTICAL_LINE_DEFAULTS.CIRCLE_TOP - VERTICAL_LINE_DEFAULTS.CIRCLE_RADIUS - hoverDelta,
                    orgWidth: VERTICAL_LINE_DEFAULTS.CIRCLE_RADIUS * 2,
                    orgHeight: VERTICAL_LINE_DEFAULTS.CIRCLE_RADIUS * 2,
                    width: (VERTICAL_LINE_DEFAULTS.CIRCLE_RADIUS + hoverDelta) * 2,
                    height: (VERTICAL_LINE_DEFAULTS.CIRCLE_RADIUS + hoverDelta) * 2,
                    fillStyle: fillStyle,
                    strokeStyle: strokeStyle,
                };

                pluginState.verticalLines.currentLinesPosition[index] = {
                    start: start,
                    end: end,
                };

            });
        }

        function verticalLinePluginAfterDraw (chart) {
            const verticalLines = chart.options.verticalLines;

            pluginState.verticalLines.currentLinesPosition.forEach(function (linePosition, index) {
                const start = linePosition.start;
                const end = linePosition.end;
                const lineX = start.x;
                // Rendered hook
                if (verticalLines.events && verticalLines.events.rendered) {
                    const lastLinePosition = pluginState.verticalLines.lastLinesPosition[index];
                    if (lastLinePosition === undefined || lastLinePosition === null || lineX !== lastLinePosition) {
                        verticalLines.events.rendered({
                            start: start,
                            end: end,
                        }, index);
                    }
                }
                pluginState.verticalLines.lastLinesPosition[index] = lineX;
            });
        }

        function verticalLinePluginEventHandler (chart, event) {
            const verticalLines = chart.options.verticalLines;
            const verticalLinesEvents = verticalLines.events;
            const allowedEvents = ['click', 'mousemove', 'mouseout'];

            if (pluginState.verticalLines.currentChartId !== chart.id) {
                pluginState.verticalLines.lastLinesTextBox = [];
                pluginState.verticalLines.currentChartId = chart.id;
                return;
            }

            if (allowedEvents.indexOf(event.type) === -1) {
                return;
            }

            let isHoveringOneLineLabel = false;
            let isClicked = false;

            pluginState.verticalLines.lastLinesTextBox.forEach(function (textBox, index) {
                const line = verticalLines.lines[index];
                if (!line) {
                    return;
                }
                if (isInRect(event.x, event.y, textBox.x, textBox.y, textBox.width, textBox.height)) {
                    (event.native?.target as HTMLElement).style.cursor = 'pointer';
                    onLeave = true;
                    if (event.type === 'click' && !isClicked) {
                        if (line.data && line.data.link) {
                            window.open(line.data.link, '_blank');
                            isClicked = true;
                        }
                    }
                    if (event.type === 'mousemove') {
                        isHoveringOneLineLabel = true;
                        // In v3, tooltip options are under plugins.tooltip
                        if (chart.options.plugins?.tooltip) {
                            chart.options.plugins.tooltip.enabled = false;
                        }
                        showVerticalLineTooltip(index, chart);
                        if (verticalLinesEvents && verticalLinesEvents.mouseover) {
                            verticalLinesEvents.mouseover(line.data, index);
                        }
                    }
                }
            });

            if (!isHoveringOneLineLabel) {
                // In v3, tooltip options are under plugins.tooltip
                if (chart.options.plugins?.tooltip) {
                    chart.options.plugins.tooltip.enabled = true;
                }
                hideVerticalLineTooltip();
            }
        }

        function hasVerticalLinePlugin (chart) {
            return chart.options.verticalLines;
        }

        function showVerticalLineTooltip (index, chart) {
            const existedTooltip = pluginState.verticalLines.tooltips[index];
            const verticalLines = chart.options.verticalLines;
            const line = verticalLines.lines[index];
            const lastLinesTextBox = pluginState.verticalLines.lastLinesTextBox;
            const lineBoundRect = lastLinesTextBox[index];
            if (!existedTooltip) {
                const tooltipEl = document.createElement('span');
                const chartBoundingRect = chart.canvas.getBoundingClientRect();
                const top = chartBoundingRect.top + lineBoundRect.y;
                const left = chartBoundingRect.left + lineBoundRect.x + lineBoundRect.width / 2;
                tooltipEl.textContent = line.label.text;
                tooltipEl.setAttribute('class', 'mon-chartjs-vertical-line-tooltip');
                document.body.appendChild(tooltipEl);

                tooltipEl.style.top = top - tooltipEl.clientHeight - 5 + 'px';
                tooltipEl.style.left = left - tooltipEl.clientWidth / 2 + 'px';
                tooltipEl.classList.add('active');

                pluginState.verticalLines.tooltips[index] = {
                    el: [tooltipEl],
                    remove: function () {
                        if (tooltipEl) {
                            tooltipEl.remove();
                        }
                    },
                };

            }
        }

        function hideVerticalLineTooltip () {
            pluginState.verticalLines.tooltips.forEach(function (t: any, index) {
                if (t) {
                    t.remove();
                    pluginState.verticalLines.tooltips[index] = null;
                }
            });
        }

        //

        function isInRect (targetX, targetY, x, y, width, height) {
            return (
                targetX >= x && targetX <= x + width && targetY >= y && targetY <= y + height
            );
        }

        function isTextOutOfContainer (ctx, text, centerLineLength) {
            // Check if text width exceeds the available center line length
            return centerLineLength && ctx.measureText(text).width > centerLineLength;
        }

        function calculateScale (ctx, centerLineLength, text) {
            return centerLineLength / ctx.measureText(text).width;
        }

        function scaledX (chart, centerLineLength, text) {
            const ctx = chart.ctx;
            const width = chart.width;
            return ((width - ctx.measureText(text).width * centerLineLength / ctx.measureText(text).width) / 2) / (centerLineLength / ctx.measureText(text).width);
        }

        function scaledY (chart, centerLineLength, text, baseY) {
            const ctx = chart.ctx;
            const height = chart.height;
            return baseY * height / (centerLineLength / ctx.measureText(text).width);
        }

        function drawText (chart, ctx, text, textSize, fontWeight, topPercentage, centerLineLength) {
            let textX; let textY; let scale;
            const width = chart.width;
            const height = chart.height;

            // Save context state before any transformations
            ctx.save();

            ctx.font = fontWeight + ' ' + textSize + 'em Proxima Nova';
            if (centerLineLength && isTextOutOfContainer(ctx, text, centerLineLength)) {
                scale = calculateScale(ctx, centerLineLength, text);
                ctx.scale(scale, scale);
                textX = scaledX(chart, centerLineLength, text);
                textY = scaledY(chart, centerLineLength, text, topPercentage);
            } else {
                textX = Math.round((width - ctx.measureText(text).width) / 2);
                textY = height * topPercentage;
            }
            ctx.fillText(text, textX, textY);

            // Restore context state after drawing (removes scale transformation)
            ctx.restore();
        }

        function drawLine (ctx, startX, startY, endX, endY, color?, width?) {
            ctx.beginPath();
            ctx.moveTo(startX, startY);
            ctx.lineTo(endX, endY);
            ctx.strokeStyle = color || '#eeeeee';
            ctx.lineWidth = width || 1;
            ctx.stroke();
        }

        /**
             *
             **/
        function removeDecimals (str) {
            const regex = /\d*\.\d*/; let num;
            if (str.indexOf('/') !== -1) {
                const res = str.split('/');
                for (let i = 0; i < res.length; i++) {
                    if (res[i].indexOf('.') !== -1) {
                        num = parseFloat(res[i].match(regex)[0]);
                        if (num === 100 || num === 0) {
                            res[i] = res[i].replace(regex, parseInt(num));
                        }
                    }
                }
                return res.join('/');
            } else {
                if (str.indexOf('.') !== -1) {
                    num = parseFloat(str.match(regex)[0]);
                    if (num === 100 || num === 0) {
                        return str.replace(regex, parseInt(num));
                    }
                }
                return str;
            }

        }

    }
}
